import { el, clear } from "../components/dom.js";
import { listGroups, subtreeMembers } from "../db/groups.js";
import { listAllMedia } from "../db/media.js";
import { enableRowDrag } from "../utils/drag-drop.js";
import { handleRowDrop } from "./group-dnd.js";
import { navigate, navigateBack } from "../main.js";
import { magScreenHead, magButton, magEmpty } from "../components/magazine/kit.js";
import { magBoxSetTile } from "../components/magazine/shelf.js";

export async function renderGroupsList(root) {
	const head = magScreenHead("Collections", {
		eyebrow: "Your shelves",
		onBack: () => navigateBack("/"),
	});

	// The masthead's "+ New" affordance, kept alongside the title.
	const newBtn = magButton("+ New", { onClick: () => navigate("/groups/new") });
	const headRow = el(
		"div",
		{ style: { display: "flex", alignItems: "flex-start", gap: "10px" } },
		el("div", { style: { flex: "1", minWidth: "0" } }, head),
		el("div", { style: { paddingTop: "26px", flex: "none" } }, newBtn),
	);

	const view = el("div.view", {}, headRow);
	root.append(view);

	// A 2-column grid of box-set tiles (one per top-level collection).
	const listContainer = el("div", {
		style: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", padding: "12px 0 24px" },
	});
	view.append(listContainer);

	const allMedia = await listAllMedia();
	await renderList(listContainer, allMedia);

	// Drag one group tile onto another to nest it as a subgroup.
	enableRowDrag(listContainer, {
		onDrop: (payload) =>
			handleRowDrop(payload, {
				allMedia,
				refresh: () => renderList(listContainer, allMedia),
			}),
	});
}

async function renderList(container, allMedia) {
	clear(container);
	const groups = await listGroups();

	if (groups.length === 0) {
		container.append(
			magEmpty({
				title: "No collections yet",
				subtitle:
					"Tap “+ New” to make one, then add games, books, TV and anime to it from their detail screens.",
				actionLabel: "+ New collection",
				onAction: () => navigate("/groups/new"),
			}),
		);
		// The empty state should span the full width, not sit in one grid column.
		container.style.display = "block";
		return;
	}
	container.style.display = "grid";

	// Only top-level groups appear in the index; nested subgroups are reached by
	// drilling into their parent.
	const childIds = new Set(groups.flatMap((g) => g.child_group_ids ?? []));
	const enriched = groups
		.filter((g) => !childIds.has(g.id))
		.map((g) => ({ group: g, members: subtreeMembers(g, groups, allMedia) }))
		.sort((a, b) => (a.group.name ?? "").localeCompare(b.group.name ?? ""));

	for (const { group, members } of enriched) {
		const node = magBoxSetTile(group, members, {
			onClick: () => navigate(`/groups/${group.id}`),
		});
		// Carry the drag attributes so the existing drop-to-nest controller works.
		node.dataset.dragId = group.id;
		node.dataset.dragKind = "group";
		container.append(node);
	}
}
